import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Circle, 
  DollarSign, 
  Globe, 
  Star, 
  FileText,
  ChevronRight,
  X,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const GettingStartedChecklist = () => {
  const [setupStatus, setSetupStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('rooftrack_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadSetupStatus();
  }, []);

  const loadSetupStatus = async () => {
    try {
      setIsLoading(true);
      const headers = getAuthHeaders();
      
      // Check visibility first
      const visibilityRes = await axios.get(`${BACKEND_URL}/api/setup/checklist-visible`, { headers });
      if (!visibilityRes.data.visible) {
        setIsVisible(false);
        setIsLoading(false);
        return;
      }
      
      // Get setup status
      const statusRes = await axios.get(`${BACKEND_URL}/api/setup/status`, { headers });
      setSetupStatus(statusRes.data);
      
      // Auto-hide if all complete
      if (statusRes.data.all_complete) {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error loading setup status:', error);
      // Don't show checklist if there's an error (user might not be logged in)
      setIsVisible(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/setup/dismiss-checklist`, {}, { headers });
      setIsDismissed(true);
      setTimeout(() => setIsVisible(false), 300);
    } catch (error) {
      console.error('Error dismissing checklist:', error);
    }
  };

  if (isLoading || !isVisible || !setupStatus) {
    return null;
  }

  const steps = [
    {
      id: 'rates',
      title: 'Set Up Your Rates',
      description: 'Configure labor and material pricing',
      completed: setupStatus.rates_configured,
      href: '/dashboard/my-rates',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      id: 'leadgen',
      title: 'Configure Lead Generation',
      description: 'Create your landing page for leads',
      completed: setupStatus.leadgen_configured,
      href: '/dashboard/leadgen',
      icon: Globe,
      color: 'text-blue-600'
    },
    {
      id: 'reviews',
      title: 'Add Review Platform URLs',
      description: 'Set up Google, Facebook, Yelp links',
      completed: setupStatus.reviews_configured,
      href: '/dashboard/reviews',
      icon: Star,
      color: 'text-yellow-500'
    },
    {
      id: 'estimate',
      title: 'Create Your First Estimate',
      description: 'Generate a roof measurement',
      completed: setupStatus.has_estimate,
      href: '/dashboard/estimates/new',
      icon: FileText,
      color: 'text-purple-600'
    }
  ];

  const completedSteps = steps.filter(s => s.completed).length;
  const progressPercent = (completedSteps / steps.length) * 100;

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 mb-6 transition-all duration-300 ${isDismissed ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Getting Started</h3>
            <p className="text-sm text-gray-600">Complete these steps to get the most out of HomeFixer360</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">
            {completedSteps} of {steps.length} complete
          </span>
          <span className="text-sm font-semibold text-blue-600">
            {Math.round(progressPercent)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2.5 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {steps.map((step) => {
          const Icon = step.icon;
          return (
            <Link
              key={step.id}
              to={step.href}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                step.completed
                  ? 'bg-green-50 border-green-200 cursor-default'
                  : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step.completed ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {step.completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Icon className={`w-4 h-4 ${step.color}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.completed ? 'text-green-700 line-through' : 'text-gray-900'}`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500 truncate">{step.description}</p>
              </div>
              {!step.completed && (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </Link>
          );
        })}
      </div>

      {/* Help Link */}
      <div className="mt-4 pt-3 border-t border-blue-200 flex items-center justify-between">
        <Link 
          to="/tutorial" 
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          Need help? View tutorials
          <ChevronRight className="w-4 h-4" />
        </Link>
        {completedSteps === steps.length && (
          <span className="text-sm text-green-600 font-medium flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            All set! You're ready to go
          </span>
        )}
      </div>
    </div>
  );
};

export default GettingStartedChecklist;
